import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useBankAccounts, useBankAccountSummaries } from '../../hooks/useBank';
import { useAccountsWithBalances } from '../../hooks/useAccounts';
import { useActiveCompanyId } from '../../stores/useCompanyStore';
import { formatCurrency, humanizeLabel, negateMoney, sumMoneyStrings } from '../../lib/formatters';

export const Route = createFileRoute('/_auth/bank/overview')({
  component: BankOverview,
});

function BankOverview() {
  const companyId = useActiveCompanyId();
  const navigate = useNavigate();
  const { data: bankData, isLoading } = useBankAccounts(companyId);
  const { data: summaries = [] } = useBankAccountSummaries(companyId);
  const { data: accounts = [] } = useAccountsWithBalances(companyId, { includeInactive: true });

  if (!companyId) {
    return (
      <div className="card" style={{ textAlign: 'center', padding: 32 }}>
        <h1 style={{ marginTop: 0 }}>Bank Transactions</h1>
        <p className="muted">No active company.</p>
        <Link to="/companies" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>
          Go to Companies
        </Link>
      </div>
    );
  }

  const bankAccounts = (bankData?.bankAccounts ?? []).filter((b) => b.isActive);
  const acctById = new Map(accounts.map((a) => [a.id, a]));
  const summaryById = new Map(summaries.map((s) => [s.bankAccountId, s]));

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 24, margin: 0 }}>Bank Transactions</h1>
        <p className="muted" style={{ margin: '4px 0 0', fontSize: 13 }}>
          Each connected account: your book balance vs. the bank feed, and how many transactions are
          waiting to be categorized. Click a card to review and match, categorize, or exclude them.
        </p>
      </div>

      {isLoading && <p className="muted">Loading…</p>}

      {!isLoading && bankAccounts.length === 0 && (
        <div className="card" style={{ textAlign: 'center', padding: 32 }}>
          <p className="muted" style={{ marginTop: 0 }}>No connected bank accounts yet.</p>
          <Link to="/bank" className="btn-primary" style={{ display: 'inline-block', textDecoration: 'none' }}>
            Go to Bank accounts
          </Link>
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 16,
        }}
      >
        {bankAccounts.map((b) => {
          const gl = acctById.get(b.accountId);
          const s = summaryById.get(b.id);
          const bookBalance = gl?.balance ?? '0';
          const toReview = s?.unmatchedCount ?? 0;

          // The bank's OWN balance, captured at the last sync. This must never
          // be derived from bookBalance: a figure computed from the books can
          // only ever agree with them, which is how a duplicated entry sat in
          // 618 Cherokee overstating both by $2,249.40 with the two tiles
          // showing identical numbers.
          const bankBalance = b.plaidCurrentBalance;

          // Unmatched transactions are real bank activity not yet posted, so a
          // difference of exactly that size is expected, not an error. Compare
          // the bank against the books ADJUSTED for what's still in the queue.
          const unmatchedSum = s?.unmatchedAmountSum ?? '0';
          const delta = gl?.normalBalance === 'credit' ? unmatchedSum : negateMoney(unmatchedSum);
          const expected = sumMoneyStrings([bookBalance, delta]);
          const difference = bankBalance === null ? null : sumMoneyStrings([bankBalance, negateMoney(expected)]);
          const isOff = difference !== null && parseFloat(difference) !== 0;

          return (
            <button
              key={b.id}
              type="button"
              className="card"
              onClick={() =>
                navigate({
                  to: '/bank/transactions',
                  search: { bankAccountId: b.id, status: 'unmatched' },
                })
              }
              style={{
                textAlign: 'left',
                cursor: 'pointer',
                padding: 16,
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
                border: '1px solid var(--border)',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 15, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {b.accountName}
                  </div>
                  <div className="muted" style={{ fontSize: 12 }}>
                    {b.institutionName ?? humanizeLabel(b.accountType)}
                    {b.accountMask && ` ····${b.accountMask}`}
                  </div>
                </div>
                {toReview > 0 ? (
                  <span className="tag warn" style={{ whiteSpace: 'nowrap' }}>{toReview} to review</span>
                ) : (
                  <span className="tag ok" style={{ whiteSpace: 'nowrap' }}>Caught up</span>
                )}
              </div>

              <div style={{ display: 'flex', gap: 16 }}>
                <div>
                  <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    Book balance
                  </div>
                  <div style={{ fontWeight: 600, fontSize: 18 }}>{formatCurrency(bookBalance, true)}</div>
                </div>
                <div>
                  <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                    Bank balance
                  </div>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 18,
                      color: isOff ? 'var(--danger, #c23b22)' : undefined,
                    }}
                  >
                    {bankBalance === null ? '—' : formatCurrency(bankBalance, true)}
                  </div>
                  <div className="muted" style={{ fontSize: 11 }}>
                    {bankBalance === null
                      ? b.isManual || b.isMock
                        ? 'not connected'
                        : 'not yet synced'
                      : b.plaidBalanceAsOf
                        ? `as of ${new Date(b.plaidBalanceAsOf).toLocaleDateString()}`
                        : ''}
                  </div>
                </div>
              </div>

              {/* Say the quiet part out loud: if the bank and the books disagree
                  by more than the not-yet-posted queue, something is wrong in
                  the ledger and the user needs to know without doing the math. */}
              {isOff && (
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    color: 'var(--danger, #c23b22)',
                    background: 'var(--danger-bg, rgba(194,59,34,0.08))',
                    borderRadius: 4,
                    padding: '6px 8px',
                  }}
                >
                  Off by {formatCurrency(difference!, true)} — the books and the bank disagree beyond
                  the {toReview} unreviewed transaction{toReview === 1 ? '' : 's'}.
                </div>
              )}

              {gl && (
                <div className="muted" style={{ fontSize: 12 }}>
                  GL: <span style={{ fontFamily: 'ui-monospace, monospace' }}>{gl.code}</span> · {gl.name}
                </div>
              )}

              <div style={{ fontSize: 13, color: 'var(--accent)', fontWeight: 500 }}>
                {toReview > 0 ? `Review ${toReview} transaction${toReview === 1 ? '' : 's'} →` : 'View transactions →'}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
